/**
 * Les mentions légales — l'identité de la maison, telle qu'elle est déclarée.
 *
 * ⚑ CE FICHIER NE CONTIENT QUE DES FAITS REÇUS. Un SIRET deviné, une forme
 * juridique supposée, un nom de dirigeant déduit d'un pseudonyme : ce sont des
 * inventions, et sur une page légale ce sont aussi des fautes. La Loi 1
 * s'applique ici plus durement qu'ailleurs, parce qu'une date fausse trompe
 * l'esprit tandis qu'une mention légale fausse engage la maison.
 *
 * Sources, dans l'ordre où elles sont arrivées le 08/09/2026 :
 *   · l'extrait de société (image) — dénomination, adresse, SIRET, APE, RCS, TVA ;
 *   · le **Kbis du 30/07/2026** — capital social, forme exacte, gérant.
 *
 * Orthographe relevée sur les documents : « Group », et non « Groupe » comme
 * le propriétaire l'avait dit à l'oral. **On recopie le papier.**
 *
 * ⚠ CE QUI N'EST PAS ICI, ET C'EST VOULU. Un Kbis porte aussi la date et le
 * lieu de naissance du gérant. Ces données n'ont RIEN à faire sur un site
 * public : la loi demande le NOM du directeur de la publication, pas son état
 * civil. On prend le champ obligatoire, et on laisse le reste dans le PDF —
 * qui n'entre pas non plus au dépôt.
 *
 * ✅ **LA LISTE DES MANQUES EST VIDE DEPUIS LE 08/09/2026, 18 h.** L'hébergeur
 * a été relevé sur une facture Railway transmise par le propriétaire. La page
 * est conforme (art. 6 III LCEN ; art. R123-237 du code de commerce), et
 * `mentionsCompletes()` rend VRAI — la seconde condition d'ouverture du site
 * est donc tenue.
 *
 * ⚠ La liste vide ne se relâche PAS pour autant : `mentionsCompletes()`
 * vérifie aussi que chaque champ obligatoire porte une valeur, de sorte
 * qu'effacer une ligne sans toucher à la liste rougisse quand même.
 */

/** L'éditeur du site — la personne morale, avec son nom commercial. */
export const EDITEUR = Object.freeze({
    denomination: 'Athea Hospitality Group',
    // Le Kbis écrit « Société à responsabilité limitée (Société à associé
    // unique) » ; la carte disait « SARL ». Les deux sont vrais, celui-ci est
    // plus précis — et c'est le registre qui fait foi.
    forme: 'SARL à associé unique',
    nomCommercial: "Le Manoir d'Autun",
    adresse: '17 avenue de la République, 71400 Autun',
    siret: '979 919 891 00023',
    ape: '5510Z',
    rcs: 'RCS Chalon-sur-Saône 979 919 891',
    tva: 'FR85 979 919 891',
    // Kbis du 30/07/2026.
    capital: '1 000,00 €',
    directeurPublication: 'Steven Athea',
    // ⚑ Le téléphone de la SOCIÉTÉ est lui aussi obligatoire (art. 6 III-1 a
    // LCEN) — je ne l'avais pas listé, et c'est Steven qui l'a comblé sans le
    // savoir en répondant à une question voisine. Une liste de manques peut
    // elle-même être incomplète : `MENTIONS_MANQUANTES` dit ce qu'on sait
    // manquer, pas ce qu'on a oublié de chercher.
    telephone: '06 71 53 82 04',
});

/**
 * Le COURRIEL public de la maison — donné par Steven le 15/09/2026.
 *
 * ⚑ IL A ATTENDU UNE HEURE PLUTÔT QUE D'ÊTRE DEVINÉ, et c'est la bonne façon
 * de l'avoir eu. La page d'attente devait porter « juste un e-mail » à la
 * place du téléphone ; la maison n'en déclarait aucun — ni le Kbis, ni la
 * facture Railway, ni le canon. Fabriquer `contact@…` aurait été l'invention
 * que la Loi 1 interdit, appliquée à une adresse : *une adresse inventée ne
 * rebondit pas, elle **disparaît**, et personne n'apprend jamais que le
 * message n'est pas arrivé.* La page n'a donc rien affiché du tout jusqu'à ce
 * que Steven la donne — une maison ne promet pas un contact qu'elle n'a pas.
 *
 * ⚠ Ce n'est PAS une mention légale au sens de la LCEN : `EDITEUR` décrit la
 * société, cette ligne-ci est la porte publique de l'hôtel. Les deux peuvent
 * un jour différer, et les confondre écrirait l'une à la place de l'autre.
 */
export const MANOIR_COURRIEL = 'lemanoirdautun@gmail.com';

/**
 * L'HÉBERGEUR — et c'est un faux ami qu'il faut nommer.
 *
 * ⚑ Sur des mentions légales, « hébergeur » ne désigne PAS le métier de la
 * maison. La loi vise l'entreprise dont les serveurs stockent le site : ici
 * **Railway**, pas Le Manoir d'Autun. Steven a lu « hébergement » au sens
 * hôtelier — son code APE est d'ailleurs 5510Z, « hôtels et hébergement
 * similaire » — et a donné l'adresse du Manoir. Y écrire cette adresse serait
 * factuellement faux, donc une invention au sens de la Loi 1.
 *
 * L'intitulé affiché a été précisé en conséquence (« Hébergeur du site »
 * plutôt que « Hébergement ») : quand un mot juste est mal lu par la personne
 * la mieux placée pour le comprendre, c'est le mot qu'il faut changer.
 */
export const HEBERGEUR = Object.freeze({
    // Relevé sur une facture Railway du 07/09/2026, transmise le 08/09.
    // ⚠ La facture porte AUSSI le courriel du client, un numéro de facture et
    // des montants. Rien de tout cela n'entre ici : la loi demande qui héberge,
    // pas ce que la maison paie. Même règle que pour le Kbis.
    denomination: 'Railway Corporation',
    adresse: '548 Market St, PMB 68956, San Francisco, California 94104, United States',
    contact: 'billing@railway.com',
});

/**
 * Ce qui manque, nommé un par un avec son motif.
 *
 * ⚑ Une liste vide serait un mensonge confortable ; une liste nommée est une
 * dette qu'on peut solder. Le test lit CETTE liste, jamais une intuition.
 *
 * ⚑ ELLE S'EST VIDÉE LE 08/09 ET ELLE ÉTAIT INCOMPLÈTE — l'audit du 10/09/2026
 * l'a montré, exactement comme l'en-tête de ce fichier le prévoyait : « une
 * liste de manques peut elle-même être incomplète ». La page n'a ni section
 * « données personnelles » ni section « cookies », alors que le site écrit
 * `medard_lang` dans le terminal du visiteur (stockage fonctionnel, exempté de
 * consentement mais pas d'information) et que le serveur tient des journaux.
 *
 * Les deux manques sont DÉCLARÉS ici, et la page les DIT. Leur texte n'est pas
 * écrit : c'est du droit, et il appartient à Steven (responsable de
 * traitement, finalités, `medard_lang`, journaux, sous-traitants, contact pour
 * les droits). `bloquant: false` : ce ne sont pas des mentions LCEN /
 * R123-237 — celles-là sont complètes — et le sceptique de l'audit a relevé
 * qu'il n'y a ni collecte active, ni formulaire, ni traceur, ni cookie non
 * exempté. Décider qu'ils FERMENT le site serait une décision juridique, pas
 * une décision d'agent : elle est laissée à Steven, ici, en toutes lettres.
 *
 * `quoiKey` est la clef i18n de la ligne affichée (sept langues) ; `quoi`
 * reste la trace française du registre.
 */
/**
 * ⚑ CE QUE LE SITE DÉPOSE ET À QUI IL PARLE — MESURÉ le 12/09/2026, au navigateur.
 *
 * **Pourquoi cet inventaire existe.** Les deux manques déclarés plus bas
 * disent que la notice « reste à rédiger par Steven », et c'est juste : le
 * responsable de traitement, les finalités et les durées sont des décisions
 * juridiques, pas des décisions d'agent. Mais une notice se rédige sur des
 * FAITS, et ces faits-là n'étaient écrits nulle part — chacun devait être
 * redécouvert par celui qui s'y mettrait.
 *
 * Relevé sur sept pages publiques, en visiteur neuf, contexte vierge à chaque
 * fois (`localStorage`, `sessionStorage`, cookies, et tout hôte appelé hors de
 * l'origine servie) :
 *
 *   · **AUCUN cookie**, sur aucune page publique ;
 *   · **une seule clef** écrite par le site : `medard_lang` — pour un visiteur
 *     NEUF ; deux autres naissent d'une adresse particulière (voir plus bas) ;
 *   · **un destinataire TIERS : `thumb.wikimedia.org`.**
 *
 * ⚠ **Ce troisième point ne figurait dans aucune de nos déclarations**, et
 * c'est le plus engageant des trois : les photographies des fiches sont
 * servies DIRECTEMENT par Wikimedia, donc **l'adresse IP et l'agent de chaque
 * visiteur partent à la Wikimedia Foundation** à chaque page illustrée, sans
 * qu'il l'ait demandé ni qu'on le lui dise. Au sens du RGPD c'est un
 * destinataire, et l'article 13 veut qu'il soit nommé. Une notice écrite sans
 * lui serait incomplète le jour de sa publication.
 *
 * Ce n'est pas une faute de conception : l'image vient de Commons parce que
 * c'est elle qui porte sa licence et son crédit (Loi 2), et la relayer depuis
 * notre serveur coûterait de la bande passante. **C'est un arbitrage** —
 * nommer le destinataire, ou le supprimer en relayant — et il appartient à
 * Steven comme le reste de la notice.
 *
 * ⚠ ET CE QUI N'EST PAS ICI : les journaux. Le journal d'accès d'uvicorn
 * porte l'adresse du pair, et `access_log_scrub.py` n'en condense QUE les
 * identifiants (salon, codes, prénoms, recherches, le QR `?session=&k=`) —
 * pas l'adresse. L'hébergeur (Railway) tient en outre
 * les siens, hors d'atteinte de ce processus, comme ce module le dit déjà.
 *
 * `surface` dit OÙ la clef est écrite : le site public en écrit trois ; les
 * autres appartiennent à l'app et au chantier, et un visiteur du site ne les
 * rencontre jamais. La distinction compte pour une notice qui ne parle que du
 * site.
 *
 * ⚠ COMPLÉTÉ LE 11/10/2026 PAR LE BALAYAGE DES SOURCES — ET IL MANQUAIT DIX
 * CLEFS. Le relevé du 12/09 se faisait au navigateur, en visiteur neuf : il
 * était juste pour ce qu'il regardait, et ne pouvait pas voir ce qui ne naît
 * que d'un geste (réclamer un séjour, allumer une Lanterne, entrer au Cabinet,
 * ouvrir la porte du site, arriver par un QR, revenir une seconde fois). Le
 * test qui devait les rattraper (`collecteMesuree.test.js`) ne lisait que les
 * appels `localStorage.setItem` écrits en toutes lettres — or ces modules
 * écrivent par un stockage injecté (`storage?.setItem`). Les dix sont
 * déclarées ci-dessous, et le balayage lit désormais toutes les formes.
 *
 * ⚑ TROIS DE CES CLEFS PORTENT UN NOM DE PERSONNE — `medard-stay` (le nom de
 * l'invité), `medard-cabinet` (le nom et l'institution du chercheur), et
 * `medard-lantern` (un libellé qui peut en être un). Ce sont des données
 * personnelles stockées dans le terminal de la personne elle-même, après un
 * geste de sa part : la notice devra le dire. Le dire ici n'est pas le rédiger.
 */
export const COLLECTE_MESUREE = Object.freeze({
    mesureLe: '2026-09-12',
    // Le balayage des sources qui complète le relevé au navigateur.
    balayeLe: '2026-10-11',
    cookies: Object.freeze([]),
    stockageNavigateur: Object.freeze([
        Object.freeze({ clef: 'medard_lang', surface: 'site', type: 'localStorage',
            pourquoi: 'la langue choisie par le visiteur, pour ne pas la redemander' }),
        Object.freeze({ clef: 'medard.visitorName', surface: 'app', type: 'localStorage',
            pourquoi: "le prénom donné à Médard, s'il en donne un" }),
        Object.freeze({ clef: 'medard_accueil_seen_v1', surface: 'app', type: 'localStorage',
            pourquoi: "ne pas rejouer l'accueil à chaque ouverture" }),
        Object.freeze({ clef: 'medard_eveil_seen', surface: 'app', type: 'sessionStorage',
            pourquoi: "ne pas rejouer l'éveil dans la même session" }),
        Object.freeze({ clef: 'medard.launchSeen', surface: 'app', type: 'sessionStorage',
            pourquoi: "ne pas rejouer l'ouverture dans la même session" }),
        Object.freeze({ clef: 'medard-veillee-intro', surface: 'projection', type: 'sessionStorage',
            pourquoi: "ne pas rejouer l'intro de la veillée" }),
        Object.freeze({ clef: 'medard_persona', surface: 'app', type: 'localStorage',
            pourquoi: 'le registre choisi — Médard le conteur ou le Professeur' }),
        Object.freeze({ clef: 'medard-veillee-narration', surface: 'projection', type: 'localStorage',
            pourquoi: 'la voix ou le silence, choisi par la salle pour la veillée' }),
        Object.freeze({ clef: 'chantier-token', surface: 'chantier', type: 'localStorage',
            pourquoi: 'la clef que le chantier porte à la main — surface privée' }),
        Object.freeze({ clef: 'chantier-lang', surface: 'chantier', type: 'localStorage',
            pourquoi: 'la langue du chantier — surface privée' }),
        // ── Complété le 11/10/2026 par le balayage des sources ──────────────
        Object.freeze({ clef: 'medard.site', surface: 'site', type: 'localStorage',
            pourquoi: "la porte de la vitrine pour ce navigateur : « 1 » après le lien privé (`?site=1`) ou le mot de passe de la porte, « 0 » après `?site=0`" }),
        Object.freeze({ clef: 'medard-pulse-arrival', surface: 'site', type: 'sessionStorage',
            pourquoi: "compter UNE fois par session l'arrivée par un QR imprimé (`?src=`) — la clef ne contient que « 1 » ; posée par l'App sur toute route" }),
        Object.freeze({ clef: 'medard-stay', surface: 'app', type: 'localStorage',
            pourquoi: "le séjour réclamé avec un code (« La Clef ») : jeton, suite, NOM DE L'INVITÉ, date de départ — valable jusqu'au départ + 24 h, comme le jeton côté serveur" }),
        Object.freeze({ clef: 'medard-cabinet', surface: 'app', type: 'localStorage',
            pourquoi: "l'admission au Cabinet d'étude : jeton, NOM et INSTITUTION du chercheur, date d'expiration" }),
        Object.freeze({ clef: 'medard-lantern', surface: 'app', type: 'localStorage',
            pourquoi: "une Lanterne reconnue : jeton, libellé du coffret, date d'expiration" }),
        Object.freeze({ clef: 'medard_visited_v1', surface: 'app', type: 'localStorage',
            pourquoi: "savoir qu'on est déjà venu, pour ne montrer l'annonce de l'Œil qu'à une visite suivante" }),
        Object.freeze({ clef: 'medard_scan_spotlight_v1', surface: 'app', type: 'localStorage',
            pourquoi: "ne montrer qu'une fois l'annonce « L'Œil de Médard s'ouvre »" }),
        Object.freeze({ clef: 'medard_ramparts3d', surface: 'app', type: 'localStorage',
            pourquoi: "le choix d'afficher ou non l'enceinte en 3D dans « Autun en 3D » (`?ramparts=`)" }),
        Object.freeze({ clef: 'medard_autun3d_narration', surface: 'app', type: 'localStorage',
            pourquoi: "la narration d'« Autun en 3D », activée ou coupée" }),
        Object.freeze({ clef: 'medard_autun3d_caption_collapsed', surface: 'app', type: 'localStorage',
            pourquoi: "la légende d'« Autun en 3D », repliée ou dépliée" }),
        // ── Ajouté le 12/10/2026 : le limiteur compte par appareil ──────────
        // Surface « site » : la touche « Écouter » d'une fiche publique passe
        // par la même porte (`speakStream`) que l'app, et la pose donc aussi.
        Object.freeze({ clef: 'medard.clientKey', surface: 'site', type: 'localStorage',
            pourquoi: "un aléa tiré au premier envoi (une écoute, une question), qui départage les appareils d'un même réseau dans le compteur de débit du serveur — sans quoi tout l'hôtel, derrière une seule adresse, partagerait un seul compteur ; il n'identifie personne ; côté serveur il ne vit que dans la mémoire de ce compteur — ni en base, ni dans les journaux — et s'efface au redémarrage ou au balayage des compteurs inactifs" }),
        // ── Ajouté le 15/10/2026 : les écrans fixes se mettent à jour seuls ──
        Object.freeze({ clef: 'medard.unattendedReload', surface: 'projection', type: 'sessionStorage',
            pourquoi: "sur la vitrine du hall et la TV de suite seulement : la version vers laquelle l'écran vient de se recharger et l'heure, pour ne jamais recharger en boucle — aucune donnée de personne" }),
    ]),
    destinatairesTiers: Object.freeze([
        Object.freeze({
            hote: 'thumb.wikimedia.org',
            quoi: "les photographies des fiches, servies directement par Wikimedia",
            recoit: "l'adresse IP et l'agent du visiteur, à chaque page illustrée",
            pourquoi: "l'image vient de Commons parce que c'est elle qui porte sa licence et son crédit (Loi 2)",
        }),
    ]),
});

/** Les clefs que le SITE PUBLIC écrit — le seul périmètre d'une notice de site. */
export const clefsDuSite = () =>
    COLLECTE_MESUREE.stockageNavigateur.filter((c) => c.surface === 'site');

export const MENTIONS_MANQUANTES = Object.freeze([
    {
        cle: 'donnees-personnelles',
        quoiKey: 'siteLegalManqueDonnees',
        quoi: "Données personnelles — la notice d'information reste à rédiger.",
        pourquoi: "le site tient des journaux serveur, écrit une préférence de langue dans le navigateur, ET envoie l'IP de chaque visiteur à un tiers (`thumb.wikimedia.org`, voir COLLECTE_MESUREE) ; un visiteur doit pouvoir lire ce qu'on fait de lui (RGPD, art. 13)",
        ou: "à rédiger par Steven : responsable de traitement, finalités, durée, destinataires, contact pour exercer ses droits. Les FAITS sont relevés dans `COLLECTE_MESUREE` ci-dessus — il n'y a plus à les rechercher, seulement à décider",
        bloquant: false,
    },
    {
        cle: 'cookies',
        quoiKey: 'siteLegalManqueCookies',
        quoi: 'Cookies et stockage local — la notice reste à rédiger.',
        pourquoi: "le site n'a aucun cookie ni traceur — MESURÉ le 12/09, zéro sur sept pages publiques — mais il écrit `medard_lang` dans le stockage local (et, selon l'adresse d'arrivée, `medard.site` et `medard-pulse-arrival` — balayage du 11/10 ; et `medard.clientKey` dès la première écoute d'une fiche — 12/10) : un stockage fonctionnel est exempté de consentement, pas d'information",
        ou: 'à rédiger par Steven, dans la même notice que les données personnelles',
        bloquant: false,
    },
]);

/** Les manques qui, seuls, interdisent d'ouvrir le site (LCEN / R123-237). */
export const mentionsBloquantes = () => MENTIONS_MANQUANTES.filter((m) => m.bloquant);

/**
 * ⚠ CE QUI NE DOIT JAMAIS ENTRER ICI, quelle que soit la source.
 *
 * Les deux documents qui ont rempli cette page — le Kbis et une facture
 * Railway — portent bien plus que ce que la loi demande : date et lieu de
 * naissance du gérant, courriel du client, numéro de facture, montants payés.
 * **La tentation, quand on recopie un document, est de tout recopier.** Un
 * test lit cette liste et refuse qu'aucun de ces motifs apparaisse dans la
 * page ni dans les constantes ci-dessus.
 */
export const JAMAIS_PUBLIE = Object.freeze([
    { quoi: "l'état civil du gérant", motif: 'la loi demande son NOM, pas sa naissance' },
    { quoi: 'le courriel du client sur la facture', motif: "il n'identifie pas l'hébergeur" },
    { quoi: 'les numéros et montants de facture', motif: 'ce que la maison paie ne regarde personne' },
]);

/**
 * Vrai quand plus aucune mention OBLIGATOIRE (LCEN, R123-237) ne manque.
 * Les manques déclarés `bloquant: false` sont affichés sur la page mais ne
 * ferment pas le site — voir `MENTIONS_MANQUANTES`.
 */
export function mentionsCompletes() {
    return mentionsBloquantes().length === 0
        && Boolean(EDITEUR.capital)
        && Boolean(EDITEUR.telephone)
        && Boolean(EDITEUR.directeurPublication)
        && Boolean(HEBERGEUR.denomination)
        && Boolean(HEBERGEUR.adresse);
}

/**
 * Les lignes d'identité à afficher, dans l'ordre où on les lit :
 * `[clef, clefI18nDuTerme, valeur]`.
 *
 * ⚑ Le TERME est une clef i18n (le libellé se traduit, la valeur jamais — un
 * SIRET est un SIRET dans les sept langues), et la valeur ne le répète pas :
 * la page les rend en `<dt>` / `<dd>`. Jusqu'à l'audit du 10/09, la liste de
 * définitions n'avait aucun terme et chaque valeur portait son libellé en dur
 * (« SIRET 979… », « Capital social 1 000,00 € ») — du HTML invalide, et un
 * libellé en français dans les six autres langues.
 */
export function lignesEditeur() {
    return [
        ['denomination', 'siteLegalDtDenomination', `${EDITEUR.denomination} — ${EDITEUR.forme}`],
        ['adresse', 'siteLegalDtAdresse', EDITEUR.adresse],
        ['siret', 'siteLegalDtSiret', `${EDITEUR.siret} — ${EDITEUR.ape}`],
        ['rcs', 'siteLegalDtRcs', EDITEUR.rcs],
        ['tva', 'siteLegalDtTva', EDITEUR.tva],
        ...(EDITEUR.capital ? [['capital', 'siteLegalDtCapital', EDITEUR.capital]] : []),
        ...(EDITEUR.telephone ? [['telephone', 'siteLegalDtTelephone', EDITEUR.telephone]] : []),
    ];
}
